// File validation constants
export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
export const ALLOWED_FILE_TYPES = {
  PDF: {
    mimeType: "application/pdf",
    extensions: [".pdf"],
  },
};

/**
 * Validates if a file is a PDF
 * @param {File} file - The file to validate
 * @returns {boolean} - True if the file is a PDF
 */
export const isPDFFile = (file) => {
  return (
    file.type === ALLOWED_FILE_TYPES.PDF.mimeType ||
    ALLOWED_FILE_TYPES.PDF.extensions.some((ext) =>
      file.name.toLowerCase().endsWith(ext)
    )
  );
};

/**
 * Validates if a file size is within the allowed limit
 * @param {File} file - The file to validate
 * @param {number} maxSize - Maximum file size in bytes (default: MAX_FILE_SIZE)
 * @returns {boolean} - True if the file size is valid
 */
export const isValidFileSize = (file, maxSize = MAX_FILE_SIZE) => {
  return file.size <= maxSize;
};

/**
 * Validates a single file and returns validation result
 * @param {File} file - The file to validate
 * @param {number} maxSize - Maximum file size in bytes (default: MAX_FILE_SIZE)
 * @returns {{isValid: boolean, error: string|null}} - Validation result
 */
export const validateFile = (file, maxSize = MAX_FILE_SIZE) => {
  if (!isPDFFile(file)) {
    return {
      isValid: false,
      error: `${file.name} - Only PDF files are allowed`,
    };
  }

  if (!isValidFileSize(file, maxSize)) {
    return {
      isValid: false,
      error: `${file.name} - File size must not exceed ${
        maxSize / (1024 * 1024)
      }MB`,
    };
  }

  return { isValid: true, error: null };
};

/**
 * Validates multiple files and separates them into valid and invalid
 * @param {File[]} files - Array of files to validate
 * @param {number} maxSize - Maximum file size in bytes (default: MAX_FILE_SIZE)
 * @returns {{validFiles: File[], invalidFiles: Array<{file: File, error: string}>}} - Validation results
 */
export const validateFiles = (files, maxSize = MAX_FILE_SIZE) => {
  const validFiles = [];
  const invalidFiles = [];

  files.forEach((file) => {
    const validation = validateFile(file, maxSize);
    if (validation.isValid) {
      validFiles.push(file);
    } else {
      invalidFiles.push({ file, error: validation.error });
    }
  });

  return { validFiles, invalidFiles };
};

/**
 * Creates a FileList object from an array of files
 * @param {File[]} files - Array of files
 * @returns {FileList} - FileList object
 */
export const createFileList = (files) => {
  const dataTransfer = new DataTransfer();
  files.forEach((file) => {
    dataTransfer.items.add(file);
  });
  return dataTransfer.files;
};
